import SidebarClient from "../../component/sideBarClient/SideBarClient";
import Axios from "axios";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FormFieldRenderer from "../../component/formFieldRenderer/FormFieldRenderer";
import { isFieldVisible, groupFieldsBySteps } from "../../utils/formConditional";
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

function isMissing(value) {
    return value === undefined || value === null || value === "";
}

function stepTitle(title, isFrench) {
    if (title && typeof title === "object") {
        return isFrench ? (title.fr || title.en) : (title.en || title.fr);
    }
    return title;
}

export default function FillForm() {
    const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;
    const { id, formId } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState();
    const [answers, setAnswers] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        Axios({
            method: "GET",
            withCredentials: true,
            url: `${backEndUrl}/auth`,
        }).then((res) => {
            setData(res.data);
            const form = (res.data.formsAssigned || []).find((f) => f._id === formId);
            if (form) {
                const initialAnswers = {};
                form.fields.forEach((field) => {
                    initialAnswers[field.fieldKey] = field.answer ?? "";
                });
                setAnswers(initialAnswers);
            }
        });
    }, [backEndUrl, formId]);

    if (data === undefined) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-slate-200 border-t-sky-500 rounded-full animate-spin" />
                    <p className="text-sm text-slate-400">Loading…</p>
                </div>
            </div>
        );
    }

    const form = (data.formsAssigned || []).find((f) => f._id === formId);
    const isFrench = data.language === 'french';

    if (!form) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
                <p className="text-slate-500">{isFrench ? 'Formulaire introuvable.' : 'Form not found.'}</p>
            </div>
        );
    }

    const readOnly = form.status !== 'not started';
    const formName = form.name && typeof form.name === 'object'
        ? (isFrench ? (form.name.fr || form.name.en) : (form.name.en || form.name.fr))
        : form.name;

    const stepGroups = groupFieldsBySteps(form.fields, form.steps);

    const handleFieldChange = (fieldKey, value) => {
        setAnswers((prev) => ({ ...prev, [fieldKey]: value }));
        setError("");
    };

    const submitForm = () => {
        setSubmitting(true);
        const payload = {
            client_id: data._id,
            formId: form._id,
            answers: form.fields.map((field) => ({
                fieldKey: field.fieldKey,
                answer: answers[field.fieldKey],
            })),
        };

        Axios.put(`${backEndUrl}/updateClient/submitForm`, payload, {
            withCredentials: true,
        }).then(() => {
            setSubmitting(false);
            navigate(`/upload/${id}`);
        }).catch((err) => {
            console.error(err);
            setSubmitting(false);
            setError(isFrench ? "Une erreur s'est produite." : "Something went wrong.");
        });
    };

    const visibleFieldsForStep = (group) => (group.fields || []).filter((f) => isFieldVisible(f, answers));

    const handleNext = () => {
        const visibleFields = visibleFieldsForStep(stepGroups[currentStep]);
        const missingFields = visibleFields.filter((field) => field.required && isMissing(answers[field.fieldKey]));
        if (missingFields.length > 0) {
            console.warn('[FillForm] missing required fields:', missingFields.map((f) => ({
                fieldKey: f.fieldKey,
                label: f.label,
                currentValue: answers[f.fieldKey],
            })));
            const labels = missingFields.map((f) => (typeof f.label === 'object' ? (f.label.fr || f.label.en) : f.label));
            setError((isFrench
                ? 'Veuillez remplir tous les champs obligatoires.'
                : 'Please fill out all required fields.') + ' — DEBUG: ' + labels.join(' | '));
            return;
        }
        setError("");
        if (currentStep === stepGroups.length - 1) {
            submitForm();
        } else {
            setCurrentStep((s) => s + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        setError("");
        setCurrentStep((s) => Math.max(0, s - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const progressPct = stepGroups.length > 0 ? Math.round(((currentStep + 1) / stepGroups.length) * 100) : 100;

    return (
        <div className="home">
            <Backdrop sx={{ color: '#fff', zIndex: 1301, position: 'fixed' }} open={submitting}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <SidebarClient companyName={data.companyName} />

            <div className="homeContainer">
                <div className="min-h-screen bg-[#F8FAFC] pb-24 font-sans">
                    <div className="max-w-3xl mx-auto px-6 mt-10">

                        <button
                            onClick={() => navigate(`/upload/${id}`)}
                            className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-700 mb-8 transition-colors"
                        >
                            <ArrowLeftIcon className="h-4 w-4" />
                            {isFrench ? 'Retour' : 'Back'}
                        </button>

                        <h1 className="text-3xl font-light text-slate-900 mb-2">
                            <span className="font-bold">{formName}</span>
                        </h1>

                        {readOnly && (
                            <p className="flex items-center gap-2 text-sm text-emerald-600 font-semibold mb-8">
                                <CheckCircleIcon className="h-4 w-4" />
                                {isFrench ? 'Formulaire soumis' : 'Form submitted'}
                            </p>
                        )}

                        {!readOnly && stepGroups.length > 1 && (
                            <div className="mb-8">
                                <div className="flex justify-between items-end mb-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
                                        {stepTitle(stepGroups[currentStep].title, isFrench) || `${isFrench ? 'Étape' : 'Step'} ${currentStep + 1}`}
                                    </span>
                                    <span className="text-xs font-semibold text-slate-400">
                                        {isFrench ? 'Étape' : 'Step'} {currentStep + 1} / {stepGroups.length}
                                    </span>
                                </div>
                                <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-sky-500 transition-all duration-500 ease-out"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {!readOnly ? (
                            <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 p-8 md:p-10 space-y-6">
                                {visibleFieldsForStep(stepGroups[currentStep]).map((field) => (
                                    <FormFieldRenderer
                                        key={field.fieldKey}
                                        field={field}
                                        value={answers[field.fieldKey]}
                                        onChange={handleFieldChange}
                                        readOnly={false}
                                        isFrench={isFrench}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {stepGroups.map((group) => {
                                    const visible = visibleFieldsForStep(group);
                                    if (visible.length === 0) return null;
                                    return (
                                        <div key={group.stepKey} className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 p-8 md:p-10 space-y-6">
                                            {group.title && (
                                                <h2 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em]">
                                                    {stepTitle(group.title, isFrench)}
                                                </h2>
                                            )}
                                            {visible.map((field) => (
                                                <FormFieldRenderer
                                                    key={field.fieldKey}
                                                    field={field}
                                                    value={answers[field.fieldKey]}
                                                    readOnly
                                                    isFrench={isFrench}
                                                />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {error && (
                            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mt-6">
                                {error}
                            </p>
                        )}

                        {!readOnly && (
                            <div className="mt-8 flex justify-between">
                                <Button
                                    onClick={handleBack}
                                    disabled={currentStep === 0 || submitting}
                                    variant="outlined"
                                    color="inherit"
                                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.5 }}
                                >
                                    {isFrench ? 'Précédent' : 'Back'}
                                </Button>
                                <Button
                                    onClick={handleNext}
                                    variant="contained"
                                    disabled={submitting}
                                    endIcon={currentStep === stepGroups.length - 1 ? <CheckCircleIcon className="h-4 w-4" /> : <ArrowRightIcon className="h-4 w-4" />}
                                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, background: '#0ea5e9', boxShadow: '0 4px 14px 0 rgba(14,165,233,0.35)', px: 4, py: 1.5, '&:hover': { background: '#0284c7' } }}
                                >
                                    {currentStep === stepGroups.length - 1
                                        ? (isFrench ? 'Soumettre le formulaire' : 'Submit Form')
                                        : (isFrench ? 'Suivant' : 'Next')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
